import { TipoEscopoEnum } from '@shared/enums/motor-fiscal/tipo-escopo.enum';

export function mensagemNcmRedundante(
    novo: string,
    existentes: string[],
    formatar: (codigo: string) => string,
): string | null {
    const codigo = novo.replace(/\D/g, '');
    const ncms = existentes.map(ncm => ncm.replace(/\D/g, ''));
    const cobridor = ncms.find(ncm => ncm.length < codigo.length && codigo.startsWith(ncm));
    if (cobridor) {
        return `O NCM ${formatar(codigo)} já está coberto pelo NCM ${formatar(cobridor)}, presente nesta exceção. Mantenha apenas o NCM mais genérico.`;
    }

    const cobertos = ncms.filter(ncm => codigo.length < ncm.length && ncm.startsWith(codigo));
    return cobertos.length > 0
        ? `O NCM ${formatar(codigo)} já cobre o(s) NCM(s) ${cobertos.map(formatar).join(', ')}, presente(s) nesta exceção. Remova os NCMs específicos antes de adicionar o mais genérico.`
        : null;
}

export function encontrarExcecaoEmConflito<T extends {
    nome: string;
    dataVigenciaInicio: string;
    dataVigenciaFim?: string | null;
    isAtivo?: boolean;
    escopos: { tipoEscopo: TipoEscopoEnum; valoresEscopo: string[] }[];
}>(excecao: T, existentes: T[]): T | undefined {
    if (!excecao.isAtivo) return undefined;
    const escopos = agruparEscopos(excecao.escopos);

    return existentes.find(existente => existente.isAtivo
        && vigenciasSobrepoem(excecao, existente)
        && haConflitoSemDesempate(escopos, agruparEscopos(existente.escopos)));
}

function agruparEscopos(escopos: { tipoEscopo: TipoEscopoEnum; valoresEscopo: string[] }[]): Map<number, string[]> {
    const grupos = new Map<number, string[]>();
    for (const escopo of escopos) {
        const valores = escopo.valoresEscopo
            .map(valor => escopo.tipoEscopo === TipoEscopoEnum.Ncm ? valor.replace(/\D/g, '') : valor.trim())
            .filter(Boolean);
        if (valores.length > 0) {
            grupos.set(escopo.tipoEscopo, [...new Set([...(grupos.get(escopo.tipoEscopo) ?? []), ...valores])]);
        }
    }
    return grupos;
}

function haConflitoSemDesempate(a: Map<number, string[]>, b: Map<number, string[]>): boolean {
    const tiposA = [...a.keys()].sort((x, y) => x - y);
    const tiposB = [...b.keys()].sort((x, y) => x - y);
    if (tiposA.length !== tiposB.length || tiposA.some((tipo, i) => tipo !== tiposB[i])) return false;

    // NCMs pai/filho têm prioridades diferentes. Só códigos iguais empatam em uma região comum.
    return tiposA.every(tipo => a.get(tipo)!.some(valor => b.get(tipo)!.includes(valor)));
}

function vigenciasSobrepoem(
    a: { dataVigenciaInicio: string; dataVigenciaFim?: string | null },
    b: { dataVigenciaInicio: string; dataVigenciaFim?: string | null },
): boolean {
    return a.dataVigenciaInicio.substring(0, 10) <= (b.dataVigenciaFim?.substring(0, 10) ?? '9999-12-31')
        && b.dataVigenciaInicio.substring(0, 10) <= (a.dataVigenciaFim?.substring(0, 10) ?? '9999-12-31');
}
